import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Box,
    Typography,
    InputAdornment,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, CloudUpload, Delete as DeleteIcon } from '@mui/icons-material';
import { showSuccess, showError } from '../utils/Alerts';
import { UserContext } from '../contexts/UserContext';

const AddVehicleModal = ({ open, handleClose, vehicle, addVehicle: addVehicleProp, setUpdatedRecord }) => {
    const { token, user } = useContext(UserContext);
    const isEditMode = !!vehicle;

    const [formData, setFormData] = useState({
        dealerID: user?.DealerID,
        vin: '',
        make: '',
        model: '',
        year: '',
        trim: '',
        bodyType: '',
        exteriorColor: '',
        interiorColor: '',
        mileage: '',
        price: '',
        costPrice: '',
        transmission: 'Automatic',
        fuelType: 'Gasoline',
        engineSize: '',
        cylinders: '',
        drivetrain: '',
        doors: '',
        seats: '',
        condition: 'Used',
        status: 'available',
        features: '',
        description: '',
        photos: [],
    });

    const [vinDecoding, setVinDecoding] = useState(false);
    const [photoFiles, setPhotoFiles] = useState([]);
    const [photoPreviews, setPhotoPreviews] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (vehicle) {
            setFormData({
                ...vehicle,
                features: Array.isArray(vehicle.features) ? vehicle.features.join(', ') : vehicle.features || '',
            });
            setPhotoPreviews(vehicle.photos || []);
        }
    }, [vehicle]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleVINDecode = async () => {
        if (!formData.vin || formData.vin.length !== 17) {
            showError('Please enter a valid 17-character VIN');
            return;
        }

        setVinDecoding(true);

        // Simulate VIN decoding with mock data
        setTimeout(() => {
            // Mock decoded data - you can customize this
            const mockData = {
                make: 'Honda',
                model: 'Accord',
                year: '2021',
                trim: 'EX-L',
                bodyType: 'Sedan',
                transmission: 'Automatic',
                fuelType: 'Gasoline',
                engineSize: '2.0L',
                cylinders: '4',
                drivetrain: 'FWD',
                doors: '4',
            };

            setFormData(prev => ({
                ...prev,
                make: mockData.make || prev.make,
                model: mockData.model || prev.model,
                year: mockData.year || prev.year,
                trim: mockData.trim || prev.trim,
                bodyType: mockData.bodyType || prev.bodyType,
                transmission: mockData.transmission || prev.transmission,
                fuelType: mockData.fuelType || prev.fuelType,
                engineSize: mockData.engineSize || prev.engineSize,
                cylinders: mockData.cylinders || prev.cylinders,
                drivetrain: mockData.drivetrain || prev.drivetrain,
                doors: mockData.doors || prev.doors,
            }));

            showSuccess('VIN decoded successfully!');
            setVinDecoding(false);
        }, 1000);
    };

    const handlePhotoChange = (e) => {
        const files = Array.from(e.target.files);
        setPhotoFiles(prev => [...prev, ...files]);

        // Create previews
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleRemovePhoto = (index) => {
        setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
        setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        // Validation
        if (!formData.vin || !formData.make || !formData.model || !formData.year || !formData.price) {
            showError('Please fill in all required fields (VIN, Make, Model, Year, Price)');
            return;
        }

        setLoading(true);

        // Simulate save with timeout
        setTimeout(() => {
            const vehiclePayload = {
                ...formData,
                vehicleID: isEditMode ? vehicle.vehicleID : `VEH-${Date.now()}`,
                features: formData.features ? formData.features.split(',').map(f => f.trim()) : [],
                mileage: parseInt(formData.mileage) || 0,
                price: parseFloat(formData.price) || 0,
                costPrice: parseFloat(formData.costPrice) || 0,
                year: parseInt(formData.year) || 0,
                doors: parseInt(formData.doors) || 0,
                seats: parseInt(formData.seats) || 0,
                dateAdded: isEditMode ? formData.dateAdded : new Date().toISOString(),
                photos: photoPreviews,
            };

            if (isEditMode) {
                setUpdatedRecord(vehiclePayload);
                showSuccess('Vehicle updated successfully!');
            } else {
                addVehicleProp(vehiclePayload);
                showSuccess('Vehicle added successfully!');
            }

            handleCloseModal();
            setLoading(false);
        }, 500);
    };

    const handleCloseModal = () => {
        setFormData({
            dealerID: user?.DealerID,
            vin: '',
            make: '',
            model: '',
            year: '',
            trim: '',
            bodyType: '',
            exteriorColor: '',
            interiorColor: '',
            mileage: '',
            price: '',
            costPrice: '',
            transmission: 'Automatic',
            fuelType: 'Gasoline',
            engineSize: '',
            cylinders: '',
            drivetrain: '',
            doors: '',
            seats: '',
            condition: 'Used',
            status: 'available',
            features: '',
            description: '',
            photos: [],
        });
        setPhotoFiles([]);
        setPhotoPreviews([]);
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEditMode ? 'Edit Vehicle' : 'Add New Vehicle'}
                <IconButton
                    aria-label="close"
                    onClick={handleCloseModal}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    {/* VIN */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="VIN *"
                            name="vin"
                            value={formData.vin}
                            onChange={handleInputChange}
                            inputProps={{ maxLength: 17 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            onClick={handleVINDecode}
                                            disabled={vinDecoding || formData.vin.length !== 17}
                                            size="small"
                                        >
                                            {vinDecoding ? <CircularProgress size={20} /> : 'Decode'}
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Enter 17-character VIN and click Decode to auto-fill details"
                        />
                    </Grid>

                    {/* Basic Info */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Make *"
                            name="make"
                            value={formData.make}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Model *"
                            name="model"
                            value={formData.model}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Year *"
                            name="year"
                            type="number"
                            value={formData.year}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Trim"
                            name="trim"
                            value={formData.trim}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Body Type"
                            name="bodyType"
                            value={formData.bodyType}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    {/* Pricing */}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Price *"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={handleInputChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Cost Price"
                            name="costPrice"
                            type="number"
                            value={formData.costPrice}
                            onChange={handleInputChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>

                    {/* Mileage & Colors */}
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Mileage"
                            name="mileage"
                            type="number"
                            value={formData.mileage}
                            onChange={handleInputChange}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">miles</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Exterior Color"
                            name="exteriorColor"
                            value={formData.exteriorColor}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Interior Color"
                            name="interiorColor"
                            value={formData.interiorColor}
                            onChange={handleInputChange}
                        />
                    </Grid>

                    {/* Mechanical Specs */}
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Transmission</InputLabel>
                            <Select
                                name="transmission"
                                value={formData.transmission}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="Automatic">Automatic</MenuItem>
                                <MenuItem value="Manual">Manual</MenuItem>
                                <MenuItem value="CVT">CVT</MenuItem>
                                <MenuItem value="Semi-Automatic">Semi-Automatic</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Fuel Type</InputLabel>
                            <Select
                                name="fuelType"
                                value={formData.fuelType}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="Gasoline">Gasoline</MenuItem>
                                <MenuItem value="Diesel">Diesel</MenuItem>
                                <MenuItem value="Electric">Electric</MenuItem>
                                <MenuItem value="Hybrid">Hybrid</MenuItem>
                                <MenuItem value="Plug-in Hybrid">Plug-in Hybrid</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Engine Size"
                            name="engineSize"
                            value={formData.engineSize}
                            onChange={handleInputChange}
                            placeholder="e.g., 2.0L"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Cylinders"
                            name="cylinders"
                            type="number"
                            value={formData.cylinders}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Drivetrain"
                            name="drivetrain"
                            value={formData.drivetrain}
                            onChange={handleInputChange}
                            placeholder="e.g., AWD, FWD, RWD"
                        />
                    </Grid>

                    {/* Additional Specs */}
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Doors"
                            name="doors"
                            type="number"
                            value={formData.doors}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Seats"
                            name="seats"
                            type="number"
                            value={formData.seats}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel>Condition</InputLabel>
                            <Select
                                name="condition"
                                value={formData.condition}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="New">New</MenuItem>
                                <MenuItem value="Used">Used</MenuItem>
                                <MenuItem value="Certified Pre-Owned">Certified Pre-Owned</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="available">Available</MenuItem>
                                <MenuItem value="reserved">Reserved</MenuItem>
                                <MenuItem value="sold">Sold</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Features */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Features"
                            name="features"
                            value={formData.features}
                            onChange={handleInputChange}
                            multiline
                            rows={2}
                            placeholder="Enter features separated by commas (e.g., Leather Seats, Sunroof, Navigation)"
                        />
                    </Grid>

                    {/* Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                        />
                    </Grid>

                    {/* Photos */}
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Photos
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUpload />}
                                sx={{ mb: 2 }}
                            >
                                Upload Photos
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                />
                            </Button>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {photoPreviews.map((preview, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            width: 100,
                                            height: 100,
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <img
                                            src={preview}
                                            alt={`Preview ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 4,
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'error.dark' },
                                            }}
                                            onClick={() => handleRemovePhoto(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update Vehicle' : 'Add Vehicle')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddVehicleModal;
